import { ADMIN_URL, JOURNAL_URL } from "@op/entities";
import { SocialLinkShare, SocialShare, SocialVkButton } from "@op/ui";
import Blocks from "editorjs-blocks-react-renderer";
import moment from "moment";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { MutableRefObject, useEffect, useRef, useState } from "react";

import Clap from "../../components/Clap";
import renderers from "../../components/Editorjs/renderers";
import * as ArticleController from "../../controllers/article-controller";
import UserStore from "../../store/UserStore";
import { useAsyncLoadImage } from "../../utils/hooks/useAsyncLoadImage";
import { openHref } from "../../utils/openHref";
import { Schema } from "./Schema";

const HashtagLink = dynamic(() => import("../../components/HashtagLink"), {
  ssr: false,
});
interface IProps {
  article?: Nullable<ArticleDetail>;
  author?: Nullable<AuthorResponse>;
  isScrollingRef?: MutableRefObject<boolean>;
  onFinish?: () => void;
  onPathChange?: (article: ArticleDetail) => void;
}

function Article({
  article,
  author,
  isScrollingRef,
  onFinish,
  onPathChange,
}: IProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const [data, setData] = useState(article);
  const { isAuth } = UserStore;

  const blocksRendererRef = useRef<HTMLDivElement | null>(null);
  const articleContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (window && articleContainerRef.current) {
      const handleScrollInsideArticle = () => {
        if (isScrollingRef?.current) {
          return;
        }

        // viewport inside the articleContainer
        const { top, bottom } =
          articleContainerRef.current!.getBoundingClientRect();

        if (
          top < 0 &&
          bottom > 0 &&
          window.location.pathname.replace(router.basePath, "") !==
            `/${article?.article_index}/`
        ) {
          article && onPathChange?.(article);
        }
      };

      const handleScrollBelowArticle = () => {
        // viewport riches bottom of the articleContainer
        const { top, height } =
          articleContainerRef.current!.getBoundingClientRect();

        if (top + height * 0 - window.innerHeight < 0) {
          // viewport riches bottom of the articleContainer
          window.removeEventListener("scroll", handleScrollBelowArticle);
          ArticleController.increaseViewCountArticle(article!.article_id);
          onFinish && onFinish();
        }
      };

      window.addEventListener("scroll", handleScrollInsideArticle, {
        passive: true,
      });
      window.addEventListener("scroll", handleScrollBelowArticle, {
        passive: true,
      });
      return () => {
        window.removeEventListener("scroll", handleScrollInsideArticle);
        window.removeEventListener("scroll", handleScrollBelowArticle);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onBlocksClick = (event: Event) => {
      event.preventDefault();
      const href = (event.target as HTMLLinkElement).href;
      openHref(href);
    };
    const blocksElement = blocksRendererRef.current;
    blocksElement?.addEventListener("click", onBlocksClick);

    return () => {
      blocksElement?.removeEventListener("click", onBlocksClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkScrollFinished = () => {
      const rect = articleContainerRef.current?.getBoundingClientRect();
      if (rect && Math.abs(rect?.top) < 50 && isScrollingRef) {
        isScrollingRef.current = false;
      } else {
        requestAnimationFrame(checkScrollFinished);
      }
    };
    const handlePopState = (event: PopStateEvent) => {
      if (
        event.state.as === `/journal/${article?.article_index}/` &&
        isScrollingRef
      ) {
        isScrollingRef.current = true;

        articleContainerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        requestAnimationFrame(checkScrollFinished);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [article?.article_index, isScrollingRef]);

  const imageUrl = useAsyncLoadImage(article?.cover_image);

  if (!data || !author) return null;

  const handleLike = async () => {
    if (article?.article_id) {
      await ArticleController.likeArticle(article_id);
      setData({
        ...data,
        likes: likes + 1,
      });
    }
  };

  const {
    article_name: title,
    article_id,
    tagslug,
    date,
    cover_image,
    content,
    likes,
    view_counts,
    modified_date_time,
  } = data;

  const shareParams = {
    url: `${JOURNAL_URL}/${article?.article_index}`,
    title,
  };

  return (
    <>
      <Schema data={data} author={author} />
      <article className="article" ref={articleContainerRef}>
        <div className="article-page__article-top mobile-hidden">
          <div className="article-data">
            <div className="article-data__item">
              <HashtagLink taguslug={tagslug} />
            </div>
            <div className="article-data__item">
              <time className="article-date">
                <span className="article-date__desktop">
                  {moment(date).format("DD MMMM YYYY")}
                </span>
              </time>
            </div>
            {modified_date_time && moment(modified_date_time).isAfter(date) && (
              <div className="article-data__item">
                <time className="article-date">
                  <span className="article-date__desktop">
                    {t("Updated")}:{" "}
                    {moment(modified_date_time).format("DD MMMM YYYY")}
                  </span>
                </time>
              </div>
            )}
          </div>
          <SocialShare
            id="desktop-share"
            shareParams={shareParams}
            translations={{
              buttonText: t("SocialShare.Share"),
              copyText: t("SocialShare.Copy"),
              VK: t("VK"),
            }}
          />
        </div>

        <h1 className="prime-title article-page__title">{title}</h1>

        <div className="author article-page__visitor">
          {author?.avatar && (
            <div className="author__avatar-block">
              <span
                className="author__avatar"
                style={{
                  backgroundImage: `url(${author.avatar})`,
                }}
              />
            </div>
          )}
          {author?.initials && !author?.avatar && (
            <div className="author__avatar-block">
              <span className="author__avatar-initials">{author.initials}</span>
            </div>
          )}
          <div className="author__data">
            {author?.name && (
              <span className="author__name">{author?.name}</span>
            )}
            <div className="article-page__article-data-mobile">
              <div className="article-page__article-data-item">
                <time className="article-date">
                  <span className="article-date__mobile">
                    {moment(date).format("DD.MM.YYYY")}
                  </span>
                </time>
              </div>
            </div>
          </div>
          <SocialShare
            className="share-mobile desktop-hidden"
            id="mobile-share"
            shareParams={shareParams}
            translations={{
              buttonText: t("SocialShare.Share"),
              copyText: t("SocialShare.Copy"),
              VK: t("VK"),
            }}
          />
        </div>
        <div className="admin article__body">
          <figure>
            <span className="fancybox">
              {!imageUrl ? (
                <span className="skeleton" data-url={cover_image}></span>
              ) : (
                <span
                  style={{
                    backgroundImage: `url(${imageUrl})`,
                  }}
                />
              )}
            </span>
          </figure>
          <div ref={blocksRendererRef}>
            <Blocks data={content} renderers={renderers} />
          </div>
        </div>
        <div className="article-signature-mobile desktop-hidden">
          <div className="author">
            {author?.avatar && (
              <div className="author__avatar-block">
                <span
                  className="author__avatar"
                  style={{
                    backgroundImage: `url(${author.avatar})`,
                  }}
                />
              </div>
            )}
            {author?.initials && !author?.avatar && (
              <div className="author__avatar-block">
                <span className="author__avatar-initials">
                  {author.initials}
                </span>
              </div>
            )}
            <div className="author__data">
              {author?.name && (
                <span className="author__name">{author.name}</span>
              )}
              <div className="article-data article-signature-mobile__info">
                <div className="article-data__item article-signature-mobile__info-item">
                  <time className="article-date">
                    <span className="article-date__mobile">
                      {moment(date).format("DD MMMM YYYY")}
                    </span>
                  </time>
                </div>
              </div>
            </div>
          </div>
          <div className="article-signature-mobile__row">
            <div className="article-signature-mobile__row-item">
              <HashtagLink taguslug={tagslug} />
            </div>
            <div className="article-signature-mobile__row-item">
              <div className="article-views">
                <em className="icon icon-views article-views__icon" />
                <span className="article-views__text">{view_counts}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="article-signature">
          <div className="article-signature__top">
            <Clap likes={likes} onClick={handleLike} />
            <div className="article-signature__soc-block">
              <SocialVkButton
                shareParams={shareParams}
                className="article-signature__soc-link"
              />
              <SocialLinkShare className="article-signature__soc-link" />
            </div>
          </div>
          <div className="article-signature__bottom mobile-hidden">
            <div className="article-data">
              <div className="article-data__item">
                <HashtagLink taguslug={tagslug} />
              </div>
              <div className="article-data__item mobile-hidden">
                <time className="article-date">
                  <span className="article-date__desktop">
                    {moment(date).format("DD MMMM YYYY")}
                  </span>
                </time>
              </div>
            </div>
            <div className="article-views article-signature__bottom-text">
              <em className="icon icon-views article-views__icon" />
              <span className="article-views__text">{view_counts}</span>
            </div>
          </div>
        </div>
        {isAuth && (
          <>
            <div className="editor__page-holder">
              <a
                href={`${ADMIN_URL}#${article?.article_index}`}
                className="btn btn-shadow editor__toggle-btn"
              >
                <span className="btn__text">{t("EditArticle")}</span>
              </a>
            </div>
          </>
        )}
      </article>
    </>
  );
}
export default Article;
